import Colors from '@constants/Colors';
import InPersonEventCard from '@molecules/InPersonEventCard';
import { CommunityHeader } from '@organisms/CommunityHeader';
import {
  QueryKeys,
  useBulkGetCommunities,
  useBulkGetParentCommunityIds,
  useGetCommunityBySlug,
  useGetEventsByCommunityIds,
  useGetSubcommunityIds,
} from '@sunrise-ui/api-client';
import {
  Button,
  CacheAvailabilityProvider,
  Loader,
  TextBold,
  TextReg,
  defaultBack,
  useDynamicStyles,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import { DateTime } from 'luxon';
import React, { useMemo } from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';

const { communitiesKey, byIdKey, parentsKey } = QueryKeys.communities;

interface CommunityViewProps {
  communitySlug: string;
}

export default function CommunityView({ communitySlug }: CommunityViewProps) {
  const { isLoading: isLoadingCommunity, data: community } =
    useGetCommunityBySlug(communitySlug);
  const styles = useDynamicStyles(createStyles);

  // Fetch child community IDs to get events from all descendant communities
  const { data: childCommunityIds = [], isLoading: isLoadingChildren } =
    useGetSubcommunityIds(community?.id);

  // Combine parent community ID with all child community IDs
  const allCommunityIdsForEvents = useMemo(() => {
    const ids = community?.id ? [community.id, ...childCommunityIds] : [];
    return [...new Set(ids)];
  }, [community?.id, childCommunityIds]);

  // Fetch all events for this community and all its children
  const { data: allEvents, isLoading: isLoadingAllEvents } =
    useGetEventsByCommunityIds({
      communityIds: allCommunityIdsForEvents,
      enabled: allCommunityIdsForEvents.length > 0 && !isLoadingChildren,
    });

  // Separate events into upcoming and past
  const { upcomingEvents, pastEvents } = useMemo(() => {
    if (!allEvents) return { upcomingEvents: [], pastEvents: [] };

    const now = DateTime.now();
    const upcoming: typeof allEvents = [];
    const past: typeof allEvents = [];

    allEvents.forEach((event) => {
      if (!event.start_date) return;
      const startDate = DateTime.fromISO(event.start_date);
      const endDate = event.scheduled_end_date
        ? DateTime.fromISO(event.scheduled_end_date)
        : null;

      // Event is upcoming if: start is in future, OR still ongoing (end date in future)
      const isUpcomingOrOngoing =
        startDate >= now || (endDate !== null && endDate >= now);

      if (isUpcomingOrOngoing) {
        upcoming.push(event);
      } else {
        past.push(event);
      }
    });

    // Sort upcoming by date ascending (soonest first)
    upcoming.sort((a, b) => {
      const dateA = DateTime.fromISO(a.start_date ?? '');
      const dateB = DateTime.fromISO(b.start_date ?? '');
      return dateA.toMillis() - dateB.toMillis();
    });

    // Sort past by date descending (most recent first)
    past.sort((a, b) => {
      const dateA = DateTime.fromISO(a.start_date ?? '');
      const dateB = DateTime.fromISO(b.start_date ?? '');
      return dateB.toMillis() - dateA.toMillis();
    });

    return { upcomingEvents: upcoming, pastEvents: past };
  }, [allEvents]);

  // Extract unique community IDs from events for bulk fetch
  const eventCommunityIds = useMemo(() => {
    const allEvents = [...(upcomingEvents ?? []), ...(pastEvents ?? [])];
    const ids = allEvents
      .map((event) => event.community_id)
      .filter((id): id is string => Boolean(id));
    return [...new Set(ids)];
  }, [upcomingEvents, pastEvents]);

  // Bulk fetch parent community IDs for events
  const { data: bulkParentCommunityIdsData } = useBulkGetParentCommunityIds({
    communityIds: eventCommunityIds,
    enabled: eventCommunityIds.length > 0,
  });

  // Extract all community IDs (event communities + their parents)
  const allCommunityIds = useMemo(() => {
    const ids = [...eventCommunityIds];
    if (bulkParentCommunityIdsData) {
      Object.values(bulkParentCommunityIdsData).forEach((parentIds) => {
        ids.push(...parentIds);
      });
    }
    return Array.from(new Set(ids));
  }, [bulkParentCommunityIdsData, eventCommunityIds]);

  // Bulk fetch community profiles
  useBulkGetCommunities({
    communityIds: allCommunityIds,
    enabled: allCommunityIds.length > 0,
  });

  const isLoadingEvents = isLoadingAllEvents || isLoadingChildren;

  if (isLoadingCommunity) {
    return (
      <View style={styles.loadingContainer}>
        <Loader size="large" />
      </View>
    );
  }

  if (!isLoadingCommunity && !community) {
    return (
      <View style={styles.notFoundContainer}>
        <View style={styles.notFoundContent}>
          <TextReg style={styles.notFoundTitle}>Community Not Found</TextReg>
          <TextReg style={styles.notFoundMessage}>
            We couldn&apos;t find this community.
          </TextReg>
          <TextReg style={styles.notFoundSubMessage}>
            The community may have been moved, deleted, or the URL might be
            incorrect.
          </TextReg>

          <View style={styles.notFoundActions}>
            <Button onPress={defaultBack()} style={styles.primaryButton}>
              Go Back
            </Button>
          </View>
        </View>
      </View>
    );
  }

  return (
    <CacheAvailabilityProvider
      queryKeys={
        new Set([
          [communitiesKey, byIdKey],
          [communitiesKey, parentsKey, true],
        ])
      }
    >
      <ScrollView
        style={styles.scrollView}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={true}
      >
        {/* Desktop top padding */}
        <View style={styles.desktopTopPadding} />

        {/* Community Header */}
        <CommunityHeader
          community={community}
          isLoadingCommunity={isLoadingCommunity}
        />

        {/* Events Section */}
        <View style={styles.eventsSection}>
          <TextBold style={styles.sectionTitle}>Events</TextBold>

          {/* Upcoming Events */}
          {isLoadingEvents ? (
            <View style={styles.loaderContainer}>
              <Loader size="small" />
            </View>
          ) : upcomingEvents && upcomingEvents.length > 0 ? (
            <View style={styles.eventsGrid}>
              {upcomingEvents.map((event) => (
                <InPersonEventCard
                  key={event.id}
                  eventId={event.id!}
                  title={event.name}
                  locationId={event.event_location_id}
                  startDate={DateTime.fromISO(event.start_date!)}
                  endDate={
                    event.scheduled_end_date
                      ? DateTime.fromISO(event.scheduled_end_date)
                      : undefined
                  }
                  communityId={event.community_id}
                  ticketingPlatformUrl={event.ticketing_platform_url}
                  style={styles.eventCard}
                  hideParentCommunities
                />
              ))}
            </View>
          ) : (
            <View style={styles.emptyStateContainer}>
              <View style={styles.emptyStateIcon}>
                <TextReg style={styles.emptyStateIconText}>📅</TextReg>
              </View>
              <TextBold style={styles.emptyStateTitle}>
                No Upcoming Events
              </TextBold>
              <TextReg style={styles.emptyStateMessage}>
                Check back later for new events from this community.
              </TextReg>
            </View>
          )}

          {/* Past Events */}
          {pastEvents && pastEvents.length > 0 && (
            <View style={styles.pastEventsSection}>
              <TextBold style={styles.sectionTitle}>Past Events</TextBold>
              <View style={styles.eventsGrid}>
                {pastEvents.slice(0, 6).map((event) => (
                  <InPersonEventCard
                    key={event.id}
                    eventId={event.id!}
                    title={event.name}
                    locationId={event.event_location_id}
                    startDate={DateTime.fromISO(event.start_date!)}
                    endDate={
                      event.scheduled_end_date
                        ? DateTime.fromISO(event.scheduled_end_date)
                        : undefined
                    }
                    communityId={event.community_id}
                    ticketingPlatformUrl={event.ticketing_platform_url}
                    style={styles.eventCard}
                    hideParentCommunities
                  />
                ))}
              </View>
            </View>
          )}
        </View>

        {/* Footer spacing */}
        <View style={styles.footer} />
      </ScrollView>
    </CacheAvailabilityProvider>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    scrollView: {
      flex: 1,
    },
    scrollContent: {
      flexGrow: 1,
    },
    desktopTopPadding: {
      height: isMobile ? 0 : 24,
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      gap: 16,
      backgroundColor: Colors.dark.purple0,
    },
    notFoundContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: isMobile ? 20 : 40,
      backgroundColor: Colors.dark.purple0,
    },
    notFoundContent: {
      alignItems: 'center',
      gap: 16,
      maxWidth: isMobile ? 320 : 400,
      backgroundColor: 'rgba(255, 255, 255, 0.05)',
      borderRadius: 12,
      padding: isMobile ? 24 : 32,
    },
    notFoundTitle: {
      fontSize: 20,
      fontWeight: 'bold',
      color: Colors.dark.text,
      textAlign: 'center',
    },
    notFoundMessage: {
      fontSize: 16,
      color: Colors.dark.text,
      textAlign: 'center',
      lineHeight: 22,
    },
    notFoundSubMessage: {
      fontSize: 14,
      color: Colors.dark.text,
      opacity: 0.7,
      textAlign: 'center',
      lineHeight: 20,
    },
    notFoundActions: {
      flexDirection: isMobile ? 'column' : 'row',
      gap: 12,
      marginTop: 8,
      width: '100%',
    },
    primaryButton: {
      flex: isMobile ? undefined : 1,
    },
    eventsSection: {
      width: '100%',
      maxWidth: isMobile ? undefined : 820,
      alignSelf: 'center',
      paddingHorizontal: isMobile ? 16 : 20,
      paddingTop: 24,
      gap: 16,
    },
    sectionTitle: {
      fontSize: isMobile ? 20 : 24,
      color: Colors.dark.text,
    },
    loaderContainer: {
      paddingVertical: 40,
      alignItems: 'center',
    },
    eventsGrid: {
      gap: 16,
    },
    eventCard: {
      width: '100%',
    },
    emptyStateContainer: {
      alignItems: 'center',
      paddingVertical: 40,
      paddingHorizontal: 20,
      backgroundColor: Colors.dark.opacity05,
      borderRadius: 16,
      gap: 12,
    },
    emptyStateIcon: {
      width: 48,
      height: 48,
      borderRadius: 24,
      backgroundColor: Colors.dark.opacity10,
      justifyContent: 'center',
      alignItems: 'center',
    },
    emptyStateIconText: {
      fontSize: 24,
    },
    emptyStateTitle: {
      fontSize: 18,
      color: Colors.dark.text,
      textAlign: 'center',
    },
    emptyStateMessage: {
      fontSize: 14,
      color: Colors.dark.text,
      opacity: 0.7,
      textAlign: 'center',
      lineHeight: 20,
    },
    pastEventsSection: {
      marginTop: 32,
      gap: 16,
    },
    footer: {
      height: 40,
    },
  });
